/**
 * Virtual Background Processor
 * Uses MediaPipe Selfie Segmentation for background replacement/blur
 * 
 * This processor takes a video track and outputs a processed track with:
 * - Background blur (adjustable strength)
 * - Background image replacement
 */

const SEGMENTATION_MODEL_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/selfie_segmentation.js';

class VirtualBackgroundProcessor {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.segmenter = null;
    this.inputVideo = null;
    this.outputStream = null;
    this.animationId = null;
    this.isRunning = false;
    
    // Settings
    this.mode = 'none'; // 'none' | 'blur' | 'image'
    this.blurStrength = 8;
    this.backgroundImage = null;
    this.backgroundImageLoaded = null;
    
    // Performance
    this.lastFrameTime = 0;
    this.targetFps = 30;
    this.frameInterval = 1000 / this.targetFps;
  }

  /**
   * Initialize the processor with MediaPipe
   */
  async init() {
    if (this.segmenter) return;

    try {
      // Dynamically load MediaPipe Selfie Segmentation
      if (!window.SelfieSegmentation) {
        await this._loadScript(SEGMENTATION_MODEL_URL);
      }

      // Wait for SelfieSegmentation to be available
      if (!window.SelfieSegmentation) {
        throw new Error('MediaPipe SelfieSegmentation not loaded');
      }

      this.segmenter = new window.SelfieSegmentation({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
      });

      this.segmenter.setOptions({
        modelSelection: 1, // 0 = general, 1 = landscape (better for video calls)
        selfieMode: true,
      });

      this.segmenter.onResults(this._onSegmentationResults.bind(this));

      console.log('[VBProcessor] MediaPipe initialized');
    } catch (err) {
      console.error('[VBProcessor] Failed to initialize MediaPipe:', err);
      throw err;
    }
  }

  /**
   * Load external script dynamically
   */
  _loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Start processing a video track
   * @param {MediaStreamTrack} inputTrack - The camera video track
   * @param {Object} options - { mode, blurStrength, backgroundImageUrl }
   * @returns {MediaStreamTrack} - The processed video track
   */
  async start(inputTrack, options = {}) {
    if (this.isRunning) {
      this.stop();
    }

    this.mode = options.mode || 'none';
    this.blurStrength = options.blurStrength || 8;

    // If mode is 'none', just return the original track
    if (this.mode === 'none') {
      return inputTrack;
    }

    await this.init();

    // Load background image if needed
    if (this.mode === 'image' && options.backgroundImageUrl) {
      await this._loadBackgroundImage(options.backgroundImageUrl);
    }

    // Create video element for input
    this.inputVideo = document.createElement('video');
    this.inputVideo.srcObject = new MediaStream([inputTrack]);
    this.inputVideo.autoplay = true;
    this.inputVideo.playsInline = true;
    this.inputVideo.muted = true;

    await new Promise((resolve) => {
      this.inputVideo.onloadedmetadata = () => {
        this.inputVideo.play();
        resolve();
      };
    });

    // Create canvas for output
    const { width, height } = inputTrack.getSettings();
    this.canvas = document.createElement('canvas');
    this.canvas.width = width || 1280;
    this.canvas.height = height || 720;
    this.ctx = this.canvas.getContext('2d');

    // Start processing loop
    this.isRunning = true;
    this._processFrame();

    // Capture stream from canvas
    this.outputStream = this.canvas.captureStream(this.targetFps);
    const outputTrack = this.outputStream.getVideoTracks()[0];

    // Copy track settings
    if (outputTrack.applyConstraints) {
      try {
        await outputTrack.applyConstraints({
          width: { ideal: width },
          height: { ideal: height },
          frameRate: { ideal: this.targetFps }
        });
      } catch (e) {
        // Ignore constraint errors
      }
    }

    console.log('[VBProcessor] Started processing', { mode: this.mode, width, height });
    return outputTrack;
  }

  /**
   * Stop processing and cleanup
   */
  stop() {
    this.isRunning = false;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.inputVideo) {
      this.inputVideo.srcObject?.getTracks().forEach(t => t.stop());
      this.inputVideo.srcObject = null;
      this.inputVideo = null;
    }

    if (this.outputStream) {
      this.outputStream.getTracks().forEach(t => t.stop());
      this.outputStream = null;
    }

    this.canvas = null;
    this.ctx = null;

    console.log('[VBProcessor] Stopped');
  }

  /**
   * Update settings without restarting
   */
  updateSettings(options) {
    if (options.mode !== undefined) this.mode = options.mode;
    if (options.blurStrength !== undefined) this.blurStrength = options.blurStrength;
    if (options.backgroundImageUrl && this.mode === 'image') {
      this._loadBackgroundImage(options.backgroundImageUrl);
    }
  }

  /**
   * Load background image
   */
  async _loadBackgroundImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this.backgroundImageLoaded = img;
        resolve();
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  /**
   * Process a single frame
   */
  async _processFrame() {
    if (!this.isRunning || !this.inputVideo || !this.segmenter) return;

    const now = performance.now();
    const elapsed = now - this.lastFrameTime;

    if (elapsed >= this.frameInterval) {
      this.lastFrameTime = now - (elapsed % this.frameInterval);

      try {
        await this.segmenter.send({ image: this.inputVideo });
      } catch (err) {
        // Frame processing error - continue
      }
    }

    this.animationId = requestAnimationFrame(() => this._processFrame());
  }

  /**
   * Handle segmentation results from MediaPipe
   */
  _onSegmentationResults(results) {
    if (!this.ctx || !this.canvas || !results.segmentationMask) return;

    const { width, height } = this.canvas;

    // Clear canvas
    this.ctx.clearRect(0, 0, width, height);

    // Save context state
    this.ctx.save();

    if (this.mode === 'blur') {
      // Draw blurred background
      this.ctx.filter = `blur(${this.blurStrength}px)`;
      this.ctx.drawImage(results.image, 0, 0, width, height);
      this.ctx.filter = 'none';

      // Use segmentation mask to draw person on top
      this.ctx.globalCompositeOperation = 'destination-out';
      this.ctx.drawImage(results.segmentationMask, 0, 0, width, height);
      this.ctx.globalCompositeOperation = 'destination-over';
      this.ctx.drawImage(results.image, 0, 0, width, height);
    } else if (this.mode === 'image' && this.backgroundImageLoaded) {
      // Draw background image
      this._drawBackgroundImage(width, height);

      // Use segmentation mask to draw person on top
      this.ctx.globalCompositeOperation = 'destination-out';
      this.ctx.drawImage(results.segmentationMask, 0, 0, width, height);
      this.ctx.globalCompositeOperation = 'destination-over';
      this.ctx.drawImage(results.image, 0, 0, width, height);
    } else {
      // No processing - just draw original
      this.ctx.drawImage(results.image, 0, 0, width, height);
    }

    // Restore context state
    this.ctx.restore();
  }

  /**
   * Draw background image with cover fit
   */
  _drawBackgroundImage(canvasWidth, canvasHeight) {
    if (!this.backgroundImageLoaded) return;

    const img = this.backgroundImageLoaded;
    const imgRatio = img.width / img.height;
    const canvasRatio = canvasWidth / canvasHeight;

    let drawWidth, drawHeight, drawX, drawY;

    if (imgRatio > canvasRatio) {
      // Image is wider - fit height
      drawHeight = canvasHeight;
      drawWidth = canvasHeight * imgRatio;
      drawX = (canvasWidth - drawWidth) / 2;
      drawY = 0;
    } else {
      // Image is taller - fit width
      drawWidth = canvasWidth;
      drawHeight = canvasWidth / imgRatio;
      drawX = 0;
      drawY = (canvasHeight - drawHeight) / 2;
    }

    this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
  }
}

// Singleton instance
let processorInstance = null;

export function getVirtualBackgroundProcessor() {
  if (!processorInstance) {
    processorInstance = new VirtualBackgroundProcessor();
  }
  return processorInstance;
}

export default VirtualBackgroundProcessor;
